import {LinkButton, Text, Typography} from '@yandex-delivery/blocks'
import React from 'react'
import type {AccentStateButton} from 'src/api/types'

import {classes} from './AccentButton.styles'

type Props = {
  button: AccentStateButton
}

const SCHEME_COLOR_VARS: Record<string, string> = {
  controlMinor: 'var(--colorScheme-controlMinor, #f1f0ed)',
  controlMain: 'var(--colorScheme-control, #FCE000)',
  textMain: 'var(--colorScheme-text, #21201F)',
  textMinor: 'var(--colorScheme-textMinor, #9e9b98)',
  textOnControl: 'var(--colorScheme-textOnControl, #21201F)',
}

function resolveSchemeColor(token: string | undefined, fallback: string): string {
  if (!token) {
    return fallback
  }
  return SCHEME_COLOR_VARS[token] ?? fallback
}

/**
 * Извлекает URL из AccentStateButton.action.
 *
 * Поддерживаемые типы action:
 * - order-state-action: содержит вложенный action с type 'open_universal_link' и полем url
 * - deeplink: содержит поле deeplink с URL
 * - empty-action: не содержит URL (статичный блок, например код подтверждения)
 */
function getButtonUrl(button: AccentStateButton): string | undefined {
  const {action} = button

  if (action.type === 'order-state-action') {
    const innerAction = action.action
    if (innerAction.type === 'open_universal_link' && 'url' in innerAction) {
      return innerAction.url as string
    }
    return undefined
  }

  if (action.type === 'deeplink') {
    return action.deeplink
  }

  return undefined
}

function AccentButtonContent({button}: Props): React.ReactElement {
  return (
    <>
      <Typography
        className={classes.title}
        style={{color: resolveSchemeColor(button.title_color, 'var(--colorScheme-text, #21201F)')}}
        variant="body2"
      >
        {button.title}
      </Typography>
      {button.subtitle ? (
        <Typography
          className={classes.subtitle}
          style={{color: resolveSchemeColor(button.subtitle_color, 'var(--colorScheme-textMinor, #9e9b98)')}}
          variant="caption1"
        >
          {button.subtitle}
        </Typography>
      ) : null}
    </>
  )
}

export function AccentButton({button}: Props): React.ReactElement | null {
  const url = getButtonUrl(button)

  if (!url) {
    return (
      <div className={classes.root}>
        <div
          className={classes.plate}
          style={{
            backgroundColor: resolveSchemeColor(button.background_color, SCHEME_COLOR_VARS.controlMain),
          }}
        >
          <AccentButtonContent button={button} />
        </div>
      </div>
    )
  }

  return (
    <div className={classes.root}>
      {/* @ts-expect-error React version mismatch between workspaces — аналогично PaymentButton */}
      <LinkButton alignItems="center" fullWidth href={url} newTab variant="action">
        <Text align="center" className={classes.label}>
          {button.subtitle ? `${button.title} · ${button.subtitle}` : button.title}
        </Text>
      </LinkButton>
    </div>
  )
}
